import { TocNode } from "@/abilities/documentation/composables/document-structure.ts";
import { TocDocument, TocQuery, TocQueryVariables } from "@/shared/services/graphql/generated/consumer-graph-types.ts";
import { consumerClient } from "@/shared/services/apollo-clients/consumer-client.ts";
import { computed, Ref } from "vue";

const tocCache: Record<string, TocQuery> = {};

export const useTocLoader = (contentMapId: Ref<string>) => {
    let preloadToc: TocNode[] = [];

    const loadAllLazy = async (childPath: string, after: string, lastEntries?: TocNode[]): Promise<TocNode[]> => {
        const variables = computed<TocQueryVariables>(() => {
            return {
                filter: {
                    contentIds: [contentMapId.value],
                },
                first: 100,
                after,
                paths: childPath,
            };
        });

        const cacheKey = JSON.stringify(variables.value);
        let tocResultFromCache = tocCache[cacheKey];
        if (!tocResultFromCache) {
            const tocResult = await consumerClient.query<TocQuery>({
                query: TocDocument,
                variables: variables.value,
                fetchPolicy: "no-cache",
            });

            tocResultFromCache = tocCache[cacheKey] = tocResult.data;
        }
        if (
            !(
                tocResultFromCache?.contentMaps?.contentMaps?.length === 1 &&
                tocResultFromCache.contentMaps.contentMaps[0]?.node?.toc?.tocEntries?.length
            )
        ) {
            throw new Error("Error corrupted contentMap");
        }
        const _entries = tocResultFromCache.contentMaps.contentMaps[0]?.node?.toc?.tocEntries.map((tocEntry) => {
            const label = tocEntry?.node?.teasers?.title ?? "";
            const { path, hasChildren, childPath, id } = tocEntry;
            const tocNode: TocNode = {
                label,
                id,
                lazy: !!hasChildren,
                childPath,
                path,
            };

            return tocNode;
        });
        const entries = [...(lastEntries ?? []), ..._entries];

        if (entries.length < tocResultFromCache.contentMaps.contentMaps[0]?.node.toc?.total.count) {
            const lastCursor =
                tocResultFromCache.contentMaps.contentMaps[0]?.node.toc?.tocEntries?.at(-1)?.cursor ?? "";
            return await loadAllLazy(childPath, lastCursor, entries);
        } else {
            return entries;
        }
    };

    const loadToc = async (
        childPath: string,
        treeCallback: ((tocNode: TocNode[]) => void) | undefined,
        last: boolean,
        flatTocCache: Record<string, TocNode>,
        allowedIds?: string[]
    ) => {
        const entries = await loadAllLazy(childPath, "");

        if (childPath === "~") {
            const rootEntry = entries[0];
            flatTocCache[rootEntry.childPath] = rootEntry;
            preloadToc = entries;
        } else {
            //lazy loading
            if (treeCallback) {
                treeCallback(entries);
                return;
            }

            const tocNode = flatTocCache[childPath];

            tocNode.children = allowedIds ? entries.filter((x) => allowedIds.includes(x.id)) : entries;
            //if children are preloaded lazy has to be set to false, or else they will be loaded again
            tocNode.lazy = false;
            for (const entry of entries) {
                flatTocCache[entry.childPath] = entry;
            }
        }
        if (last) {
            return preloadToc;
        }
    };

    return { loadToc };
};
